import React, { useEffect } from 'react';

import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Switch } from 'react-router-dom';

import LayoutRoute from 'common/components/LayoutRoute';
import { PERMISSION_LIST } from 'common/constants/permissions';
import { DefaultLayout, LoginLayout } from 'common/layouts';
import {
    getIsGuest,
    getSession,
    initSessionActions,
} from 'common/utils/session';

import analytics from '../analytics';
import companyGeneral from '../companyGeneral';
import companyRoles from '../companyRoles';
import companyUsers from '../companyUsers';
import configurationDocuments from '../configurationDocuments';
import configurationInsurances from '../configurationInsurances';
import creditFile from '../creditFile';
import credits from '../credits';
import editTemplateView from '../editTemplateView';
import financial from '../financial';
import funnel from '../funnel';
import installerProfile from '../installerProfile';
import installers from '../installers';
import login from '../login';
import notifications from '../notifications';
import profile from '../profile';
import proposalPreview from '../proposalPreview';
import quotePreview from '../quotePreview';
import quotes from '../quotes';
import reportPreview from '../reportPreview';
import reportReviewPro from '../reportReviewPro';
import supplierIntegrations from '../supplierIntegrations/';
import templates from '../templates';

import PageNotFoundLayout from './components/PageNotFoundLayout';

const Container = ({ setCurrentValues }) => {
    const session = getSession();
    const isGuest = getIsGuest();

    if (!isGuest) {
        if (window.Intercom && import.meta.env.VITE_INTERCOM_APP_ID) {
            window.Intercom('boot', {
                app_id: import.meta.env.VITE_INTERCOM_APP_ID,
                created_at: Math.round(new Date().getTime() / 1000),
                email: session.email,
                name: session.first_name + ' ' + session.last_name,
            });
        }
    }

    useEffect(() => {
        if (isGuest) return;
        setCurrentValues(session);
        initSessionActions();
    }, []);

    return (
        <Switch>
            <LayoutRoute
                component={login.Container}
                isPublic
                layout={LoginLayout}
                path="/login"
            />

            <LayoutRoute
                component={companyGeneral.Container}
                exact
                layout={DefaultLayout}
                path="/company/general"
                requiredPermissions={[
                    PERMISSION_LIST.GENERAL_INFORMATION_PERMISSION,
                ]}
            />

            <LayoutRoute
                component={companyRoles.Container}
                exact
                layout={DefaultLayout}
                path="/company/roles"
                requiredPermissions={[PERMISSION_LIST.ROLES_PERMISSION]}
            />

            <LayoutRoute
                component={companyUsers.Container}
                exact
                layout={DefaultLayout}
                path="/company/users"
                requiredPermissions={[PERMISSION_LIST.USERS_PERMISSION]}
            />

            <LayoutRoute
                component={credits.Container}
                exact
                layout={DefaultLayout}
                path={['/', '/financing']}
                requiredPermissions={[
                    PERMISSION_LIST.ALLIANCE_PRODUCT_APPLICATIONS_PERMISSION,
                ]}
            />

            <LayoutRoute
                component={quotes.Container}
                exact
                layout={DefaultLayout}
                path="/quotes"
                requiredPermissions={[
                    PERMISSION_LIST.ALLIANCE_PRODUCT_APPLICATIONS_PERMISSION,
                ]}
            />

            <LayoutRoute
                component={credits.Container}
                exact
                layout={DefaultLayout}
                path={['/credits', '/credits/:id']}
                requiredPermissions={[
                    PERMISSION_LIST.ALLIANCE_PRODUCT_APPLICATIONS_PERMISSION,
                ]}
            />

            <LayoutRoute
                component={creditFile.Container}
                exact
                layout={DefaultLayout}
                path={['/credits/file', '/credits/file/:id']}
                requiredPermissions={[
                    PERMISSION_LIST.ALLIANCE_PRODUCT_APPLICATIONS_PERMISSION,
                ]}
            />

            <LayoutRoute
                component={installerProfile.Container}
                exact
                layout={DefaultLayout}
                path={['/installer-profile', '/installer-profile/:id']}
                requiredPermissions={[
                    PERMISSION_LIST.ALLIANCE_REQUESTS_PERMISSION,
                ]}
            />

            <LayoutRoute
                component={installers.Container}
                exact
                layout={DefaultLayout}
                path={['/installers', '/installers/:id']}
                requiredPermissions={[
                    PERMISSION_LIST.ALLIANCE_REQUESTS_PERMISSION,
                ]}
            />

            <LayoutRoute
                component={configurationDocuments.Container}
                exact
                layout={DefaultLayout}
                path="/configuration/documents"
                requiredPermissions={[
                    PERMISSION_LIST.ALLIANCE_DOCUMENTS_PERMISSION,
                    PERMISSION_LIST.APPLICATION_DOCUMENTS_PERMISSION,
                ]}
            />

            <LayoutRoute
                component={configurationInsurances.Container}
                exact
                layout={DefaultLayout}
                path="/configuration/insurances"
                requiredPermissions={[
                    PERMISSION_LIST.INSURANCE_DOCUMENTS_PERMISSION,
                ]}
            />

            <LayoutRoute
                component={profile.Container}
                exact
                layout={DefaultLayout}
                path={['/profile', '/profile/:id']}
            />
            <LayoutRoute
                component={financial.Container}
                exact
                layout={DefaultLayout}
                path="/financial"
                requiredPermissions={[
                    PERMISSION_LIST.ALLIANCE_PRODUCTS_PERMISSION,
                ]}
            />

            <LayoutRoute
                component={funnel.Container}
                exact
                layout={DefaultLayout}
                multiBranchMode
                path={['/funnel', '/funnel/:id']}
                requiredPermissions={[
                    PERMISSION_LIST.ALLIANCE_PRODUCT_APPLICATIONS_PERMISSION,
                ]}
            />

            <LayoutRoute
                component={notifications.Container}
                exact
                layout={DefaultLayout}
                path={['/notifications', '/notifications/:id']}
            />

            <LayoutRoute
                component={templates.Container}
                exact
                layout={DefaultLayout}
                path={['/templates']}
            />

            <LayoutRoute
                component={editTemplateView.Container}
                exact
                layout={DefaultLayout}
                path="/templates/edition/:uid"
            />

            <LayoutRoute
                component={proposalPreview.Container}
                exact
                layout={DefaultLayout}
                path="/credits/:creditId/preview/:uid"
            />

            <LayoutRoute
                component={quotePreview.Container}
                exact
                layout={DefaultLayout}
                path="/preview/:uid"
            />

            <LayoutRoute
                component={reportPreview.Container}
                exact
                layout={DefaultLayout}
                path="/report/:uid"
                requiredPermissions={[
                    PERMISSION_LIST.ALLIANCE_PRODUCT_APPLICATIONS_PERMISSION,
                ]}
            />

            <LayoutRoute
                component={reportReviewPro.Container}
                exact
                layout={DefaultLayout}
                path="/report-review-pro/:uid"
                requiredPermissions={[
                    PERMISSION_LIST.ALLIANCE_PRODUCT_APPLICATIONS_PERMISSION,
                ]}
            />

            <LayoutRoute
                component={supplierIntegrations.Container}
                exact
                layout={DefaultLayout}
                path={['/suppliers']}
                requiredPermissions={[
                    PERMISSION_LIST.GENERAL_INFORMATION_PERMISSION,
                ]}
            />

            <LayoutRoute
                component={analytics.Container}
                exact
                layout={DefaultLayout}
                path={['/analytics']}
            />

            <LayoutRoute
                component={<React.Fragment></React.Fragment>}
                layout={PageNotFoundLayout}
                path="*"
            />
        </Switch>
    );
};

Container.propTypes = { setCurrentValues: PropTypes.func };

const mapDispatchToProps = (dispatch) => ({
    setCurrentValues: (data) =>
        dispatch(profile.actions.setCurrentValues(data)),
});

export default connect(null, mapDispatchToProps)(Container);
